import { Component, effect, inject, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { IconComponent } from './ui/icon.component';
import { ModalService } from '../services/modal.service';
import { AnimateDirective } from '../directives/animate.directive';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div *ngIf="modalService.isOpen()" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div 
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        (click)="close()"
      ></div>

      <!-- Modal Content -->
      <div 
        class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100 flex flex-col max-h-[95vh] sm:max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <!-- Button Close -->
        <button 
          (click)="close()" 
          class="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
        >
          <app-icon name="x" [size]="24"></app-icon>
        </button>

        <div class="p-5 md:p-6 overflow-y-auto w-full min-h-[400px]">
          <!-- Logo Header -->
          <div class="flex justify-center md:mb-5">
            <img src="/images/logo-preta.svg" alt="Beetrack" class="h-7 md:h-8" width="120" height="32">
          </div>
          
          <div id="hubspot-modal-container" class="hubspot-custom-form"></div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in {
      animation: fadeIn 0.4s ease-out forwards;
    }

    /* Ocultar a marca d'água/badge do HubSpot */
    .hubspot-custom-form ::ng-deep .hs_form_builder_badge,
    .hubspot-custom-form ::ng-deep .hubspot-link__container,
    .hubspot-custom-form ::ng-deep .hb-form-builder-link,
    .hubspot-custom-form ::ng-deep .hs-form__virality-link,
    .hubspot-custom-form ::ng-deep [data-test-id="viral-link-iframe"] {
      display: none !important;
    }
  `]
})
export class ModalComponent {
  modalService = inject(ModalService);
  router = inject(Router);

  get formId() {
    return this.router.url.includes('/v2')
      ? '6b2e3cf8-41e3-412f-a4a0-102738a49a3a'
      : '51e076d6-2994-4810-b3f2-27d573d566fe';
  }

  constructor() {
    effect(() => {
      if (this.modalService.isOpen()) {
        setTimeout(() => {
          // Limpa o container para garantir que o form seja recriado a cada abertura
          const container = document.getElementById('hubspot-modal-container');
          if (container) container.innerHTML = '';
          this.loadHubSpotForm();
          this.observeAndRemoveWatermark();
        }, 100);
      }
    });
    this.listenForFormSubmit();
  }

  private listenForFormSubmit() {
    window.addEventListener('message', (event) => {
      // Pega os dados do evento
      const data = event.data;

      // Filtra para garantir que estamos lidando apenas com callbacks do HubSpot
      if (data && data.type === 'hsFormCallback') {

        // 'onFormSubmitted' é o evento de SUCESSO. Garante que passou na validação.
        if (data.eventName === 'onFormSubmitted') {
          const win = window as any;
          win.dataLayer = win.dataLayer || [];

          // Dispara a informação pro dataLayer
          win.dataLayer.push({
            event: 'hubspot_form_submit', // Nome exato que será lido pelo GTM
            formId: data.id || this.formId // ID do seu form
          });

          // Console log para você debugar no navegador e confirmar que funcionou
          console.log('✅ [HubSpot] Conversão disparada pro dataLayer:', win.dataLayer);
        }
      }
    });
  }

  private loadHubSpotForm() {
    // Usa o mesmo script v2.js que funciona no modal "Seja um Revendedor"
    if (!document.querySelector('script[src="//js.hsforms.net/forms/embed/v2.js"]')) {
      const script = document.createElement('script');
      script.src = '//js.hsforms.net/forms/embed/v2.js';
      script.defer = true;
      script.onload = () => this.initHubSpotForm();
      document.body.appendChild(script);
    } else {
      this.initHubSpotForm();
    }
  }

  private initHubSpotForm() {
    const win = window as any;
    if (win.hbspt) {
      const container = document.getElementById('hubspot-modal-container');
      if (container) {
        win.hbspt.forms.create({
          region: "na1",
          portalId: "51135977",
          formId: this.formId,
          target: '#hubspot-modal-container'
        });
      }
    } else {
      setTimeout(() => this.initHubSpotForm(), 500);
    }
  }

  private observeAndRemoveWatermark() {
    const observer = new MutationObserver(() => {
      const watermark = document.querySelector('.hs-form__virality-link, [data-test-id="viral-link-iframe"]');
      if (watermark) watermark.remove();

      // Aplica máscara de telefone após o campo do HubSpot aparecer
      const phoneInput = document.querySelector(
        '#form-seja-revendedor input[name="phone"], #hubspot-modal-container input[name="phone"], #hubspot-modal-container input.hs-phone'
      ) as HTMLInputElement | null;

      if (phoneInput && !phoneInput.dataset['masked']) {
        phoneInput.dataset['masked'] = 'true';
        phoneInput.placeholder = '(XX) XXXXX-XXXX';

        // Tenta forçar país Brasil no seletor nativo, se houver
        const countrySelect = phoneInput.closest('.hs-form-field')?.querySelector(
          'select[name="phone_country"], .hs-phone-country-select, select[class*="country"]'
        ) as HTMLSelectElement | null;
        if (countrySelect) {
          countrySelect.value = 'BR';
          countrySelect.dispatchEvent(new Event('change'));
          countrySelect.disabled = true;
        }

        let isFormatting = false;
        phoneInput.addEventListener('input', () => {
          if (isFormatting) return;
          // setTimeout(0) garante que roda após o HubSpot reformatar o valor
          setTimeout(() => {
            isFormatting = true;
            let digits = phoneInput.value.replace(/\D/g, '');
            // Remove código 55 se vier do flag picker (+55-XX-XXXXX-XXXX)
            if (digits.startsWith('55') && digits.length > 11) {
              digits = digits.slice(2);
            }
            phoneInput.value = this.maskBrPhone(digits);
            isFormatting = false;
          }, 0);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  private maskBrPhone(value: string): string {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }

  close() {
    this.modalService.close();
  }
}
